import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useDropzone } from 'react-dropzone';
import AnalysisResults from './AnalysisResults';
import Sidebar from './Sidebar';
import UploadButton from './UploadButton';
import SettingsPage from './SettingsPage';
import { useUploadDisabled } from '../hooks/useUploadDisabled';
import { MAX_FILE_SIZE_BYTES } from '../constants/fileTypes';
import { useFileUpload } from '../hooks/useFileUpload';
import axios from '../config/api';

interface SessionItem {
  id: string;
  name: string;
  created_at: string;
  has_analysis: boolean;
  latest_job_id: string | null;
  latest_job_status: string | null;
}

const Dashboard: React.FC = () => {
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(urlSessionId || null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [forceCollapseSidebar, setForceCollapseSidebar] = useState(false);
  const [optimisticSessions, setOptimisticSessions] = useState<SessionItem[]>([]);
  const mainContentRef = useRef<HTMLElement>(null);

  // Handle demo mode redirect
  const { isDisabled: uploadsDisabled, isLoading: uploadsLoading } = useUploadDisabled();

  useEffect(() => {
    if (uploadsDisabled && !uploadsLoading && !sessionId && isSignedIn && isLoaded) {
      const redirectToLatest = async () => {
        try {
          const token = await getToken?.();
          const response = await axios.get('/api/sessions', {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const sessions = response.data;
          if (sessions && sessions.length > 0) {
            const latest = sessions.sort((a: any, b: any) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];
            navigate(`/dashboard/session/${latest.id}`, { replace: true });
          }
        } catch (error) {
          console.error('Failed to redirect to latest session:', error);
        }
      };

      redirectToLatest();
    }
  }, [uploadsDisabled, uploadsLoading, sessionId, isSignedIn, isLoaded, getToken, navigate]);

  // Sync URL session ID with state
  useEffect(() => {
    if (urlSessionId && urlSessionId !== sessionId) {
      setSessionId(urlSessionId);
    }
  }, [urlSessionId, sessionId]);

  // Handle navigation state from lap analysis close
  useEffect(() => {
    const state = location.state as { closeSidebarOnMobile?: boolean } | null;
    if (state?.closeSidebarOnMobile) {
      setForceCollapseSidebar(true);
      navigate(location.pathname, { replace: true, state: null });
      setTimeout(() => setForceCollapseSidebar(false), 100);
    }
  }, [location.state, location.pathname, navigate]);

  const handleUploadStart = () => {};

  const handleUploadSuccess = (id: string) => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleUploadComplete = (session: SessionItem) => {
    setSessionId(session.id);
    navigate(`/dashboard/session/${session.id}`);
    setOptimisticSessions(prev =>
      prev.some(s => s.id === session.id) ? prev : [session, ...prev]
    );
    setRefreshTrigger(prev => prev + 1);

    // Safety net: drop the placeholder after 30s even if the canonical list never confirms it
    setTimeout(() => {
      setOptimisticSessions(prev => prev.filter(s => s.id !== session.id));
    }, 30000);
  };

  const handleSessionsConfirmed = useCallback((confirmedIds: string[]) => {
    setOptimisticSessions(prev => prev.filter(s => !confirmedIds.includes(s.id)));
  }, []);

  const handleAnalysisStatusChange = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleOptimisticUpdate = (callback: (sessionId: string) => void) => {
    // Sidebar registers its optimistic processing callback here
  };

  const {
    uploadFile,
    openFileDialog,
    isUploading,
    error: uploadError,
    clearError,
    showDisabledModal,
    setShowDisabledModal,
  } = useFileUpload({
    onUploadStart: handleUploadStart,
    onUploadComplete: handleUploadComplete,
    onUploadSuccess: handleUploadSuccess
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      uploadFile(acceptedFiles[0]);
    }
  }, [uploadFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE_BYTES,
    noClick: true,
    accept: {
      'text/csv': ['.csv'],
      'application/octet-stream': ['.vbo', '.log', '.gpc'],
      'text/plain': ['.log']
    }
  });

  const handleSelectSession = (session: SessionItem | null) => {
    if (session) {
      setSessionId(session.id);
      navigate(`/dashboard/session/${session.id}`);
    } else {
      setSessionId(null);
      navigate('/dashboard');
    }
  };

  const isSettingsPage = location.pathname === '/dashboard/settings';

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [sessionId]);

  return (
    <div className="dashboard">
      <div className={`app-layout ${sidebarCollapsed ? 'sidebar-collapsed-layout' : ''}`}>
        <Sidebar
          selectedSessionId={sessionId || undefined}
          onSelectSession={handleSelectSession}
          onSidebarToggle={setSidebarCollapsed}
          refreshTrigger={refreshTrigger}
          onOptimisticUpdate={handleOptimisticUpdate}
          forceCollapse={forceCollapseSidebar}
          optimisticSessions={optimisticSessions}
          onSessionsConfirmed={handleSessionsConfirmed}
          UploadButton={
            <UploadButton
              openFileDialog={openFileDialog}
              isUploading={isUploading}
              error={uploadError}
              clearError={clearError}
              showDisabledModal={showDisabledModal}
              setShowDisabledModal={setShowDisabledModal}
            />
          }
        />
        <main className={`main-content ${sessionId ? 'has-analysis' : ''}`} ref={mainContentRef}>
          {isSettingsPage ? (
            <SettingsPage />
          ) : !sessionId ? (
            <div
              {...getRootProps()}
              className={`flex items-center justify-center min-h-full ${
                isDragActive ? 'bg-indigo-900 bg-opacity-20' : ''
              }`}
            >
              <input {...getInputProps()} />
              <div className={`card max-w-2xl w-full mx-auto transition-all duration-200 ${
                isDragActive ? 'border-indigo-500 border-2 border-dashed bg-indigo-900 bg-opacity-10' : ''
              }`}>
                <h2 className="text-2xl font-semibold mb-6 text-center">Upload your track data to drive faster</h2>
                {isDragActive && (
                  <div className="text-center mb-4 text-indigo-400">
                    Drop your file here...
                  </div>
                )}
                <UploadButton
                  openFileDialog={openFileDialog}
                  isUploading={isUploading}
                  error={uploadError}
                  clearError={clearError}
                  showDisabledModal={showDisabledModal}
                  setShowDisabledModal={setShowDisabledModal}
                />
              </div>
            </div>
          ) : (
            <AnalysisResults
              sessionId={sessionId}
              onNewUpload={() => setSessionId(null)}
              onStatusChange={handleAnalysisStatusChange}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
